import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Typography,
  Paper,
  CircularProgress,
  Button,
  Box,
  List,
  ListItem,
  Divider,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import BuildIcon from '@mui/icons-material/Build';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import RequestManager from '../../utilities/requestManager';
import { StateManager } from '../../utilities/stateManager';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import firebase from '../../utilities/firebase';
import Store from '../../utilities/store';
import constants from '../../utilities/constants';
import AddInboundCarForm from './AddInboundCarForm';

const containerStyle = { width: '100%', height: '100%' };
const defaultCenter = { lat: 39.8283, lng: -98.5795 };

const skywayLocations = {
  "FL": "10420 Portal Crossing, Sarasota, FL 34211",
  "DAL": "14805 Venture Dr, Farmers Branch, TX 75234",
};

const isInbound = (car) => car.kind === 'inbound';

// Priority tier: 0 = best (funded, no service), 3 = worst (unfunded, has service)
const getPriorityTier = (car) => {
  const funded = car.funded;
  const hasService = car.service_orders && car.service_orders.length > 0;
  if (funded && !hasService) return 0;
  if (funded && hasService) return 1;
  if (!funded && !hasService) return 2;
  return 3;
};

// Map marker fill colors per tier
const TIER_COLORS = ['#2e7d32', '#f59e0b', '#e65100', '#c62828'];
const TIER_COLORS_SELECTED = ['#81c784', '#fde68a', '#ffb74d', '#ef9a9a'];

// Inbound cars have no funding tier — they ship the other direction, so they
// get their own color (the blue the process pipeline uses for inbound).
const INBOUND_COLOR = '#0288d1';
const INBOUND_COLOR_SELECTED = '#81d4fa';

const listColor = (car) => (isInbound(car) ? INBOUND_COLOR : TIER_COLORS[getPriorityTier(car)]);

const lotLabel = (code) =>
  (constants.locations.find(l => l.value === code) || {}).label || code || 'lot';

const fullAddress = (car) => [
  car.customer?.address1,
  car.customer?.city,
  car.customer?.state,
  car.customer?.zip,
].filter(Boolean).join(', ');

const makeMarkerIcon = (fillColor) => ({
  path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
  fillColor,
  fillOpacity: 1,
  strokeWeight: 1,
  strokeColor: '#fff',
  scale: 1.6,
  anchor: { x: 12, y: 24 },
});

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Compact funding + service chips used in both list and map popup
const StatusChips = ({ car, size = 'small' }) => {
  const theme = useTheme();
  const hasService = car.service_orders && car.service_orders.length > 0;

  // Inbound cars aren't funded and don't carry service orders yet — show where
  // they are headed instead.
  if (isInbound(car)) {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
        <Chip
          size={size}
          icon={<CallReceivedIcon />}
          label={`Inbound → ${lotLabel(car.destination)}`}
          sx={{
            height: 20,
            fontSize: '0.7rem',
            fontWeight: 600,
            backgroundColor: alpha(INBOUND_COLOR, 0.15),
            color: INBOUND_COLOR,
            '& .MuiChip-icon': { fontSize: 12, color: INBOUND_COLOR },
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
      <Chip
        size={size}
        icon={car.funded ? <AttachMoneyIcon /> : <MoneyOffIcon />}
        label={car.funded ? 'Funded' : 'Unfunded'}
        sx={{
          height: 20,
          fontSize: '0.7rem',
          fontWeight: 600,
          backgroundColor: car.funded
            ? alpha(theme.palette.success.main, 0.15)
            : alpha(theme.palette.warning.main, 0.15),
          color: car.funded ? theme.palette.success.dark : theme.palette.warning.dark,
          '& .MuiChip-icon': { fontSize: 12 },
        }}
      />
      {hasService && (
        <Chip
          size={size}
          icon={<BuildIcon />}
          label={`${car.service_orders.length} order${car.service_orders.length > 1 ? 's' : ''}`}
          sx={{
            height: 20,
            fontSize: '0.7rem',
            fontWeight: 600,
            backgroundColor: alpha(theme.palette.error.main, 0.12),
            color: theme.palette.error.dark,
            '& .MuiChip-icon': { fontSize: 12 },
          }}
        />
      )}
    </Box>
  );
};

// Tiny date line shown under chips
const DateLine = ({ car }) => {
  if (isInbound(car)) {
    const added = formatDate(car.sub_status_time || car.date);
    if (!added) return null;
    return (
      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25, lineHeight: 1.3 }}>
        Inbound since: {added}
      </Typography>
    );
  }

  const dealDate = formatDate(car.deal_date);
  const fundDate = formatDate(car.funding_date);
  if (!dealDate && !fundDate) return null;
  return (
    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25, lineHeight: 1.3 }}>
      {dealDate && `Deal: ${dealDate}`}
      {dealDate && fundDate && ' · '}
      {fundDate && `Funded: ${fundDate}`}
    </Typography>
  );
};

// Filter chip toggle group
const FilterGroup = ({ label, options, value, onChange, theme }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, whiteSpace: 'nowrap', fontSize: '0.7rem' }}>
      {label}:
    </Typography>
    {options.map(opt => (
      <Chip
        key={opt.value}
        label={opt.label}
        size="small"
        onClick={() => onChange(opt.value)}
        sx={{
          height: 22,
          fontSize: '0.7rem',
          cursor: 'pointer',
          fontWeight: value === opt.value ? 700 : 400,
          backgroundColor: value === opt.value
            ? alpha(theme.palette.primary.main, 0.15)
            : alpha(theme.palette.action.selected, 0.5),
          color: value === opt.value ? theme.palette.primary.dark : theme.palette.text.secondary,
          border: value === opt.value ? `1px solid ${alpha(theme.palette.primary.main, 0.4)}` : '1px solid transparent',
          '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) },
        }}
      />
    ))}
  </Box>
);

// Stable identity — the car fetch below keys off initialSelection.selectedCars,
// so a fresh literal per render would re-fetch and wipe the user's picks.
const EMPTY_SELECTION = { selectedCars: [] };

// `title` and `submitLabel` let this render either inside the load page's dialog
// ("Load Selector" / "Add to load") or as the standalone Load Planner page
// (no heading / "Create load"). Pass title={null} to drop the heading.
const LoadSelector = ({
  onLoadCreated,
  initialSelection = EMPTY_SELECTION,
  title = 'Load Selector',
  submitLabel = 'Add to load',
}) => {
  const theme = useTheme();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [map, setMap] = useState(null);
  const [selectedCars, setSelectedCars] = useState(new Set(initialSelection.selectedCars));
  const [listVisible, setListVisible] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');         // 'all' | 'sold' | 'inbound'
  const [fundingFilter, setFundingFilter] = useState('all');   // 'all' | 'funded' | 'unfunded'
  const [serviceFilter, setServiceFilter] = useState('all');   // 'all' | 'clear' | 'has'
  const [markingAsShipped, setMarkingAsShipped] = useState(new Set());
  const [isAddInboundOpen, setIsAddInboundOpen] = useState(false);
  const hoverTimeoutRef = useRef(null);
  const geocodedRef = useRef(new Set());

  // Markers are derived — removing a car from the list removes its pin.
  const markers = useMemo(() => cars
    .filter(car => car?.location?.lat && car?.location?.lng)
    .map(car => ({ position: { lat: car.location.lat, lng: car.location.lng }, car })),
  [cars]);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ['geometry', 'geocoding'],
  });

  const onLoad = useCallback((mapInstance) => setMap(mapInstance), []);
  const onUnmount = useCallback(() => setMap(null), []);

  useEffect(() => {
    const fetchCars = async () => {
      try {
        // Sold cars come from the deals pipeline (geocoded server-side); inbound
        // cars come straight off the cars collection and are geocoded below.
        const [response, inbound] = await Promise.all([
          RequestManager.get({ function: 'getCarsNeedingShipping' }),
          Store.getInboundCars().catch(error => {
            console.error('Error fetching inbound cars:', error);
            StateManager.setAlertAndOpen("Error fetching inbound cars", "error");
            return [];
          }),
        ]);

        if (!response.success) throw new Error("Error fetching cars");

        const sold = response.data
          .filter((car, i, self) => i === self.findIndex(c => c.id === car.id))
          .map(car => ({ ...car, kind: 'sold' }));

        // Sold first (by priority tier, then oldest deal), then inbound (oldest
        // first — the longer a car sits inbound, the more urgent the pickup).
        const sortedSold = sold.sort((a, b) => {
          const tierDiff = getPriorityTier(a) - getPriorityTier(b);
          if (tierDiff !== 0) return tierDiff;
          const dateA = a.deal_date ? new Date(a.deal_date).getTime() : Infinity;
          const dateB = b.deal_date ? new Date(b.deal_date).getTime() : Infinity;
          return dateA - dateB;
        });

        const soldIds = new Set(sortedSold.map(c => c.id));
        const sortedInbound = inbound
          .filter(car => !soldIds.has(car.id))
          .sort((a, b) => {
            const dateA = new Date(a.sub_status_time || a.date || 0).getTime();
            const dateB = new Date(b.sub_status_time || b.date || 0).getTime();
            return dateA - dateB;
          });

        const all = [...sortedSold, ...sortedInbound];
        setCars(all);

        const validSelected = new Set(
          initialSelection.selectedCars.filter(id => all.some(c => c.id === id))
        );
        setSelectedCars(validSelected);
      } catch (error) {
        console.error('Error fetching cars:', error);
        StateManager.setAlertAndOpen("Error fetching cars", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchCars();
  }, [initialSelection.selectedCars]);

  // Geocode inbound pickup addresses once the Maps SDK is up. Each address is
  // attempted once per session, whether or not it resolves.
  useEffect(() => {
    if (!isLoaded) return;

    const pending = cars.filter(car =>
      isInbound(car) && !car.location && !geocodedRef.current.has(car.id) && fullAddress(car)
    );
    if (pending.length === 0) return;

    pending.forEach(car => geocodedRef.current.add(car.id));
    const geocoder = new window.google.maps.Geocoder();

    (async () => {
      const results = await Promise.all(pending.map(car => new Promise(resolve => {
        geocoder.geocode({ address: fullAddress(car) }, (res, status) => {
          if (status === 'OK' && res[0]) {
            const loc = res[0].geometry.location;
            resolve({ id: car.id, location: { lat: loc.lat(), lng: loc.lng() } });
          } else {
            console.warn(`Geocoding failed for inbound car ${car.id}: ${status}`);
            resolve(null);
          }
        });
      })));

      const found = results.filter(Boolean);
      if (found.length === 0) return;

      setCars(prev => prev.map(car => {
        const hit = found.find(f => f.id === car.id);
        return hit ? { ...car, location: hit.location } : car;
      }));
    })();
  }, [isLoaded, cars]);

  const handleCarSelect = (car) => {
    setSelectedCars(prev => {
      const next = new Set(prev);
      next.has(car.id) ? next.delete(car.id) : next.add(car.id);
      return next;
    });
  };

  const handleMarkerClick = (marker) => {
    setSelectedMarker(marker);
    handleCarSelect(marker.car);
  };

  const handleMarkerHover = (marker) => {
    clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => setSelectedMarker(marker), 100);
  };

  const handleMarkerLeave = () => {
    clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => setSelectedMarker(null), 300);
  };

  const handleInfoWindowMouseEnter = () => clearTimeout(hoverTimeoutRef.current);
  const handleInfoWindowMouseLeave = () => handleMarkerLeave();

  const handleCreateLoad = async () => {
    try {
      if (!onLoadCreated) { console.warn("LoadSelector: onLoadCreated prop is not provided."); return; }

      const chosen = Array.from(selectedCars)
        .map(id => cars.find(c => c.id === id))
        .filter(Boolean);

      // Only sold cars have a deal to flag as shipped. Inbound cars have none —
      // writing one here would create a stray deal doc.
      await Promise.all(chosen.filter(car => !isInbound(car)).map(car =>
        firebase.firestore().doc(`deals/${car.id}`).set(
          { shipping_complete: true, shipping_status: 'complete' },
          { merge: true }
        )
      ));

      const carsForLoad = chosen.map(car => isInbound(car)
        ? {
          // Inbound: pick up at the seller, drop at the destination lot.
          id: car.id,
          carTitle: car.car.title,
          thumbnail: car.car.thumbnail,
          vin: car.car.vin || '',
          customerId: car.consignorId || null,
          customerName: car.customer.name,
          phone_number: car.customer.phone_number || '',
          email: car.customer.email || '',
          address: fullAddress(car),
          dropoff: skywayLocations[car.destination] || null,
          location: car.location,
          charge: car.charge || 0,
          type: 'car_pickup',
          stopType: 'pickup',
        }
        : {
          id: car.id,
          carTitle: car.car.title,
          thumbnail: car.car.thumbnail,
          customerId: car.buyer || car.cobuyer || null,
          customerName: car.customer.name,
          pickup: skywayLocations[car.location] || null,
          address: `${car.customer.address1} ${car.customer.city}, ${car.customer.state} ${car.customer.zip}`,
          location: car.location,
          type: 'car_delivery',
          stopType: 'dropoff',
        });
      onLoadCreated(carsForLoad);
    } catch (error) {
      console.error('Error in LoadSelector handleCreateLoad:', error);
      StateManager.setAlertAndOpen("Error creating load", "error");
    }
  };

  // A newly created inbound car lands at the top of the list, already selected.
  const handleInboundCreated = (car) => {
    setCars(prev => [car, ...prev]);
    setSelectedCars(prev => new Set(prev).add(car.id));
    setTypeFilter('all');
  };

  const handleThumbnailClick = (carId, e) => {
    e.stopPropagation();
    StateManager.openCarModal(carId);
  };

  // Sold cars are flagged shipped on their deal. Inbound cars have no deal, so
  // the flag goes on the car and getInboundCars skips it.
  const handleMarkAsShipped = async (car, e) => {
    e.stopPropagation();
    const carId = car.id;
    const inbound = isInbound(car);
    try {
      setMarkingAsShipped(prev => new Set(prev).add(carId));
      if (inbound) {
        await firebase.firestore().doc(`cars/${carId}`).set(
          { pickup_not_needed: true },
          { merge: true }
        );
      } else {
        await firebase.firestore().doc(`deals/${carId}`).set(
          { shipping_complete: true, shipping_status: 'complete' },
          { merge: true }
        );
      }
      setCars(prev => prev.filter(c => c.id !== carId));
      setSelectedCars(prev => { const n = new Set(prev); n.delete(carId); return n; });
      StateManager.setAlertAndOpen(
        inbound ? "Car removed from the map" : "Car marked as shipped successfully!",
        "success",
      );
    } catch (error) {
      console.error('Error removing car from the map:', error);
      StateManager.setAlertAndOpen(
        inbound ? "Error removing car from the map" : "Error marking car as shipped",
        "error",
      );
    } finally {
      setMarkingAsShipped(prev => { const n = new Set(prev); n.delete(carId); return n; });
    }
  };

  const applyFilters = (car) => {
    // Sold vs inbound
    if (typeFilter === 'sold' && isInbound(car)) return false;
    if (typeFilter === 'inbound' && !isInbound(car)) return false;
    // Funding and service describe a deal, so they only narrow sold cars.
    if (isInbound(car)) return true;
    if (fundingFilter === 'funded' && !car.funded) return false;
    if (fundingFilter === 'unfunded' && car.funded) return false;
    const hasService = car.service_orders && car.service_orders.length > 0;
    if (serviceFilter === 'clear' && hasService) return false;
    if (serviceFilter === 'has' && !hasService) return false;
    return true;
  };

  const filteredCars = cars.filter(car => {
    if (!applyFilters(car)) return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      car.id.toLowerCase().includes(q) ||
      car.car.title.toLowerCase().includes(q) ||
      car.customer.name.toLowerCase().includes(q) ||
      (car.customer.address1 && car.customer.address1.toLowerCase().includes(q)) ||
      (car.customer.city && car.customer.city.toLowerCase().includes(q)) ||
      (car.customer.state && car.customer.state.toLowerCase().includes(q)) ||
      (car.customer.zip && car.customer.zip.toLowerCase().includes(q))
    );
  });

  const soldCount = cars.filter(car => !isInbound(car)).length;
  const inboundCount = cars.length - soldCount;

  // Filtered marker IDs for map sync
  const filteredCarIds = new Set(filteredCars.map(c => c.id));
  const visibleMarkers = markers.filter(m => filteredCarIds.has(m.car.id));

  const getMarkerIcon = (car, isSelected) => {
    if (isInbound(car)) return makeMarkerIcon(isSelected ? INBOUND_COLOR_SELECTED : INBOUND_COLOR);
    const tier = getPriorityTier(car);
    return makeMarkerIcon(isSelected ? TIER_COLORS_SELECTED[tier] : TIER_COLORS[tier]);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  const TIER_LABELS = [
    { color: TIER_COLORS[0], label: 'Funded · No service' },
    { color: TIER_COLORS[1], label: 'Funded · Has service' },
    { color: TIER_COLORS[2], label: 'Unfunded · No service' },
    { color: TIER_COLORS[3], label: 'Unfunded · Has service' },
    { color: INBOUND_COLOR, label: 'Inbound pickup' },
  ];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexShrink: 0 }}>
        <Box>
          {/* No heading on the standalone page — the menu already names it. */}
          {title && <Typography variant="h4">{title}</Typography>}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: title ? 0.5 : 0 }}>
            <Typography variant="body2" color="text.secondary">
              {filteredCars.length} of {cars.length} cars shown · {soldCount} sold, {inboundCount} inbound
            </Typography>
            {/* Map legend */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              {TIER_LABELS.map(t => (
                <Box key={t.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: t.color, flexShrink: 0 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{t.label}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            color="primary"
            sx={{ height: 48 }}
            onClick={() => setIsAddInboundOpen(true)}
            startIcon={<CallReceivedIcon />}
          >
            New Inbound Car
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ height: 48, minWidth: 200 }}
            onClick={handleCreateLoad}
            disabled={selectedCars.size === 0}
            startIcon={<LocalShippingIcon />}
          >
            {submitLabel} ({selectedCars.size})
          </Button>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0, position: 'relative' }}>

        {/* Sidebar list */}
        {listVisible && (
          <Paper sx={{ width: 380, flexShrink: 0, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            {/* Sticky header inside list */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
              <Typography variant="h6" sx={{ mb: 1.5 }}>
                Stops ({selectedCars.size} selected)
              </Typography>

              {/* Search */}
              <TextField
                placeholder="Search cars..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                sx={{ mb: 1.5 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                  ),
                }}
              />

              {/* Filter row */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FilterGroup
                  label="Type"
                  value={typeFilter}
                  onChange={setTypeFilter}
                  theme={theme}
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'sold', label: `Sold (${soldCount})` },
                    { value: 'inbound', label: `Inbound (${inboundCount})` },
                  ]}
                />
                {/* Funding and service only describe sold cars. */}
                {typeFilter !== 'inbound' && (
                  <>
                    <FilterGroup
                      label="Funding"
                      value={fundingFilter}
                      onChange={setFundingFilter}
                      theme={theme}
                      options={[
                        { value: 'all', label: 'All' },
                        { value: 'funded', label: 'Funded' },
                        { value: 'unfunded', label: 'Unfunded' },
                      ]}
                    />
                    <FilterGroup
                      label="Service"
                      value={serviceFilter}
                      onChange={setServiceFilter}
                      theme={theme}
                      options={[
                        { value: 'all', label: 'All' },
                        { value: 'clear', label: 'No Orders' },
                        { value: 'has', label: 'Has Orders' },
                      ]}
                    />
                  </>
                )}
              </Box>
            </Box>

            {/* Scrollable list */}
            <List sx={{ flex: 1, overflow: 'auto', p: 0 }}>
              {filteredCars.map((car) => {
                const isSelected = selectedCars.has(car.id);
                return (
                  <React.Fragment key={car.id}>
                    <ListItem
                      onClick={() => handleCarSelect(car)}
                      sx={{
                        cursor: 'pointer',
                        alignItems: 'flex-start',
                        px: 2,
                        py: 1.25,
                        borderLeft: `3px solid ${listColor(car)}`,
                        backgroundColor: isSelected
                          ? alpha(theme.palette.primary.main, 0.06)
                          : 'transparent',
                        '&:hover': {
                          backgroundColor: isSelected
                            ? alpha(theme.palette.primary.main, 0.09)
                            : alpha(theme.palette.action.hover, 0.5),
                        },
                        transition: 'background-color 0.15s ease',
                      }}
                    >
                      {/* Thumbnail */}
                      {car.car.thumbnail && (
                        <Box
                          component="img"
                          src={car.car.thumbnail}
                          alt={car.car.title}
                          onClick={(e) => handleThumbnailClick(car.id, e)}
                          sx={{
                            width: 60,
                            height: 45,
                            objectFit: 'cover',
                            borderRadius: 1,
                            mr: 1.5,
                            flexShrink: 0,
                            cursor: 'pointer',
                            mt: 0.25,
                          }}
                        />
                      )}

                      {/* Text content */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {car.id} {car.car.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.8rem' }}>
                          {car.customer.name}
                        </Typography>
                        {fullAddress(car) ? (
                          <Typography variant="caption" color="text.disabled" noWrap sx={{ display: 'block' }}>
                            {[car.customer.address1, car.customer.city, car.customer.state].filter(Boolean).join(', ')}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="warning.dark" noWrap sx={{ display: 'block' }}>
                            No pickup address — set a consignor on the car
                          </Typography>
                        )}
                        <StatusChips car={car} />
                        <DateLine car={car} />

                        {/* Service order links */}
                        {car.service_orders && car.service_orders.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            {car.service_orders.map(so => (
                              <Chip
                                key={so.id}
                                label={so.status}
                                size="small"
                                clickable
                                onClick={e => { e.stopPropagation(); StateManager.openServiceOrderModal(so.id); }}
                                icon={<OpenInNewIcon />}
                                sx={{
                                  height: 18,
                                  fontSize: '0.65rem',
                                  textTransform: 'capitalize',
                                  backgroundColor: alpha(theme.palette.error.main, 0.08),
                                  color: theme.palette.error.dark,
                                  '& .MuiChip-icon': { fontSize: 10 },
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>

                      {/* Action buttons */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: 1, flexShrink: 0 }}>
                        <Tooltip title={isSelected ? 'Remove from load' : 'Add to load'}>
                          <IconButton
                            size="small"
                            sx={{
                              width: 28,
                              height: 28,
                              backgroundColor: isSelected
                                ? alpha(theme.palette.error.main, 0.1)
                                : alpha(theme.palette.success.main, 0.1),
                              color: isSelected ? theme.palette.error.main : theme.palette.success.main,
                              '&:hover': {
                                backgroundColor: isSelected
                                  ? alpha(theme.palette.error.main, 0.2)
                                  : alpha(theme.palette.success.main, 0.2),
                              },
                            }}
                          >
                            {isSelected ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={isInbound(car) ? 'Pickup not needed (remove from list)' : 'Mark as shipped (remove from list)'}>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMarkAsShipped(car, e)}
                            disabled={markingAsShipped.has(car.id)}
                            sx={{
                              width: 28,
                              height: 28,
                              backgroundColor: alpha(theme.palette.action.selected, 0.5),
                              color: theme.palette.text.secondary,
                              '&:hover': { backgroundColor: alpha(theme.palette.action.selected, 0.8) },
                            }}
                          >
                            {markingAsShipped.has(car.id)
                              ? <CircularProgress size={12} />
                              : <LocalShippingIcon sx={{ fontSize: 14 }} />
                            }
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                );
              })}
              {filteredCars.length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No cars match the current filters.
                  </Typography>
                </Box>
              )}
            </List>
          </Paper>
        )}

        {/* Sidebar toggle */}
        <IconButton
          onClick={() => setListVisible(v => !v)}
          size="small"
          sx={{
            position: 'absolute',
            left: listVisible ? 380 : 0,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            backgroundColor: 'white',
            width: 24,
            height: 48,
            borderRadius: '0 8px 8px 0',
            boxShadow: '2px 0 6px rgba(0,0,0,0.15)',
            '&:hover': { backgroundColor: '#f5f5f5' },
          }}
        >
          {listVisible ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>

        {/* Map */}
        {isLoaded ? (
          <Paper sx={{ flex: 1, height: '100%', minHeight: 0, overflow: 'hidden' }}>
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={defaultCenter}
              zoom={4}
              onLoad={onLoad}
              onUnmount={onUnmount}
              options={{
                zoomControl: true,
                streetViewControl: true,
                mapTypeControl: true,
                fullscreenControl: true,
                gestureHandling: 'greedy',
              }}
            >
              {visibleMarkers.map((marker, index) => (
                <Marker
                  key={index}
                  position={marker.position}
                  onClick={() => handleMarkerClick(marker)}
                  onMouseOver={() => handleMarkerHover(marker)}
                  onMouseOut={handleMarkerLeave}
                  icon={getMarkerIcon(marker.car, selectedCars.has(marker.car.id))}
                />
              ))}

              {selectedMarker && (
                <InfoWindow
                  position={selectedMarker.position}
                  onCloseClick={() => setSelectedMarker(null)}
                  options={{
                    pixelOffset: new window.google.maps.Size(0, -40),
                    disableAutoPan: true,
                  }}
                >
                  <div
                    onMouseEnter={handleInfoWindowMouseEnter}
                    onMouseLeave={handleInfoWindowMouseLeave}
                    style={{ maxWidth: 240 }}
                  >
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                      {selectedMarker.car.id} {selectedMarker.car.car.title}
                    </Typography>
                    {selectedMarker.car.car.thumbnail && (
                      <img
                        src={selectedMarker.car.car.thumbnail}
                        alt={selectedMarker.car.car.title}
                        style={{ width: '100%', height: 130, objectFit: 'cover', borderRadius: 4, marginBottom: 6, cursor: 'pointer' }}
                        onClick={(e) => handleThumbnailClick(selectedMarker.car.id, e)}
                      />
                    )}
                    <Typography variant="body2" fontWeight={500}>{selectedMarker.car.customer.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[selectedMarker.car.customer.address1, selectedMarker.car.customer.city, selectedMarker.car.customer.state]
                        .filter(Boolean).join(', ')}
                    </Typography>

                    {/* Status chips in popup */}
                    <StatusChips car={selectedMarker.car} />
                    <DateLine car={selectedMarker.car} />

                    {/* Service order links in popup */}
                    {selectedMarker.car.service_orders && selectedMarker.car.service_orders.length > 0 && (
                      <Box sx={{ mt: 0.5 }}>
                        {selectedMarker.car.service_orders.map(so => (
                          <Typography
                            key={so.id}
                            variant="caption"
                            onClick={e => { e.stopPropagation(); StateManager.openServiceOrderModal(so.id); }}
                            sx={{
                              display: 'block',
                              color: 'error.main',
                              textDecoration: 'none',
                              cursor: 'pointer',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            SO: {so.status}{so.title ? ` — ${so.title}` : ''}
                          </Typography>
                        ))}
                      </Box>
                    )}

                    <Box sx={{ mt: 1 }}>
                      <Button
                        onClick={(e) => {
                          handleCarSelect(selectedMarker.car);
                          setSelectedMarker(null);
                        }}
                        size="small"
                        variant={selectedCars.has(selectedMarker.car.id) ? 'outlined' : 'contained'}
                        color={selectedCars.has(selectedMarker.car.id) ? 'error' : 'primary'}
                        fullWidth
                        sx={{ fontSize: '0.7rem', mb: 0.5 }}
                      >
                        {selectedCars.has(selectedMarker.car.id) ? 'Remove from load' : 'Add to load'}
                      </Button>
                      <Button
                        onClick={(e) => handleMarkAsShipped(selectedMarker.car, e)}
                        disabled={markingAsShipped.has(selectedMarker.car.id)}
                        size="small"
                        variant="outlined"
                        fullWidth
                        sx={{ fontSize: '0.7rem' }}
                      >
                        {markingAsShipped.has(selectedMarker.car.id) ? <CircularProgress size={14} /> : 'Remove from map'}
                        </Button>
                    </Box>
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>
          </Paper>
        ) : (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        )}
      </Box>

      <AddInboundCarForm
        open={isAddInboundOpen}
        onClose={() => setIsAddInboundOpen(false)}
        onCreated={handleInboundCreated}
      />
    </Box>
  );
};

export default LoadSelector;
